import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import { logout, selectIsAuth, selectUsername, selectRouterId } from "@/features/auth/authSlice";
import { refreshJWT } from "@/services/authService";
import axios from "axios";

const WARN_MS = 55 * 60 * 1000;        // 55 minutes → show warning
const LOGOUT_MS = 60 * 60 * 1000;      // 60 minutes → auto logout
const JWT_REFRESH_MS = 25 * 60 * 1000; // 25 minutes → refresh JWT before it expires
const COUNTDOWN_SEC = 5 * 60;          // 5 minute countdown in warning modal
const ACTIVITY_THROTTLE_MS = 10_000;   // don't reset timers more than once per 10s

export function useInactivityTimer() {
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(selectIsAuth);
  const username = useAppSelector(selectUsername);
  const routerId = useAppSelector(selectRouterId);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SEC);
  const [sessionExpired, setSessionExpired] = useState(false);

  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jwtIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastResetRef = useRef(0);
  const lastJwtRefreshRef = useRef(Date.now());

  // Keep latest auth values in refs so async callbacks always have current values
  const usernameRef = useRef(username);
  const routerIdRef = useRef(routerId);
  useEffect(() => { usernameRef.current = username; }, [username]);
  useEffect(() => { routerIdRef.current = routerId; }, [routerId]);

  const clearInactivityTimers = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    warnTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  /**
   * Attempts a JWT refresh. If it fails, the session is unrecoverable
   * (refresh token expired or server-side revocation) — log out immediately.
   */
  const tryRefreshJWT = useCallback(async () => {
    const u = usernameRef.current;
    const r = routerIdRef.current;
    if (!u || !r) return;
    try {
      await refreshJWT(u, r);
      lastJwtRefreshRef.current = Date.now();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setSessionExpired(true);
      } else {
        console.warn("JWT refresh failed (transient)");
      }
    }
  }, []);

  const resetTimers = useCallback(() => {
    clearInactivityTimers();
    setShowWarning(false);
    setCountdown(COUNTDOWN_SEC);

    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(COUNTDOWN_SEC);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(countdownIntervalRef.current!);
            countdownIntervalRef.current = null;
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    }, WARN_MS);

    logoutTimerRef.current = setTimeout(() => {
      sessionStorage.setItem("loggedOutReason", "inactivity");
      dispatch(logout());
    }, LOGOUT_MS);
  }, [clearInactivityTimers, dispatch]);

  const stayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!isAuth) {
      clearInactivityTimers();
      if (jwtIntervalRef.current) {
        clearInterval(jwtIntervalRef.current);
        jwtIntervalRef.current = null;
      }
      setShowWarning(false);
      setSessionExpired(false);
      return;
    }

    resetTimers();
    lastJwtRefreshRef.current = Date.now();

    // Proactive JWT refresh every 25 min so the JWT never expires while the session is alive
    jwtIntervalRef.current = setInterval(() => {
      tryRefreshJWT();
    }, JWT_REFRESH_MS);

    // When the tab becomes visible again (after sleep/background), check if the JWT
    // needs an immediate refresh rather than waiting for the next interval tick.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const elapsed = Date.now() - lastJwtRefreshRef.current;
      if (elapsed >= JWT_REFRESH_MS) {
        tryRefreshJWT();
      }
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < ACTIVITY_THROTTLE_MS) return;
      lastResetRef.current = now;
      resetTimers();
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInactivityTimers();
      if (jwtIntervalRef.current) {
        clearInterval(jwtIntervalRef.current);
        jwtIntervalRef.current = null;
      }
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuth, resetTimers, clearInactivityTimers, tryRefreshJWT]);

  return { showWarning, countdown, stayLoggedIn, sessionExpired };
}
